import type { ArenaView } from '@/features/arena/model';

/**
 * OPERATIONS → DEALS, its address (`/operations/deals`): the Brand Deal Arena's list as one view of Operations.
 *
 * - `view`: All deals (`list`, the default) or Focus view (`focus`), as the Arena's own switch.
 * - `pick`: the deal (`deal:<id>`) or person (`person:<playbookId>:<platform>:<platformId>`) on Focus view's stage.
 * - `playbook`: the campaign whose deals show. Absent is every campaign at once ("All campaigns"), as the Arena opened
 *   before Operations: a link to one deal from Money or Content never lands on a campaign that hides it.
 * - `at`: one of the campaign's road stops (`negotiations`, one of hers…): only the deals standing there. Only with a
 *   campaign (a stop belongs to one campaign's road).
 * - `doc`: a deal's brief, contract or payments open in the side panel (`brief:<dealId>`, `contract:<dealId>`,
 *   `payments:<dealId>` — an operator's, where his Mark paid is).
 * - `contract`: with a contract `doc`, the contract it opens on, by its own id (a person with two open contract reviews:
 *   an operator's panel offers both, and opens on this one). Without it, the one its row's waiting line is about, else
 *   the deal's own. With no `doc` at all, only the contract (Draper's link, `ref:contract:57`): Deals reads it, finds its
 *   deal and fills in `pick` and `doc` (`operator/contract-link.ts`).
 * - `sheet`: `more`, an operator's "…" sheet open on his phone (an entry of its own, as the Road's: Back closes it).
 * - `team`: the team the link is about (a bell's or an email's link to one contract's panel). When it isn't the team
 *   the person is in, Deals switches to it first (when they have a seat on it) and opens the same address there.
 *
 * A plain validator, like Road's (`visibility-search.ts`): route validators ship in the entry chunk.
 */
export interface DealsSearch {
  view: ArenaView;
  pick?: string;
  playbook?: number;
  at?: string;
  doc?: string;
  contract?: number;
  sheet?: 'more';
  team?: number;
}

/** What a link may pass. */
export type DealsSearchInput = Partial<Record<keyof DealsSearch, unknown>>;

/** A stage's id as the road's layer writes it: a built-in stop (`negotiations`) or one of hers (`custom_mg3k1x_1`). */
const STAGE_ID = /^[A-Za-z0-9_:-]{1,64}$/;

/** What of a deal the side panel opens: its brief, its contract, or (an operator's) its payments. */
export type DealDocKind = 'brief' | 'contract' | 'payments';

/** A deal's document open in the side panel. */
const DOC = /^(brief|contract|payments):(\d{1,12})$/;

/** The deal's document a `doc` names, or null. */
export function docOf(doc: string | undefined): { kind: DealDocKind; dealId: number } | null {
  const m = doc ? DOC.exec(doc) : null;
  return m ? { kind: m[1] as DealDocKind, dealId: Number(m[2]) } : null;
}

/** The longest pick a link may carry. */
const PICK_MAX = 200;

const positiveInt = (value: unknown): number | undefined => {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : Number.NaN;
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
};

export function parseDealsSearch(input: DealsSearchInput | Record<string, unknown>): DealsSearch {
  const raw = input as Record<string, unknown>;
  const out: DealsSearch = { view: raw.view === 'focus' ? 'focus' : 'list' };
  if (typeof raw.pick === 'string' && raw.pick.length > 0 && raw.pick.length <= PICK_MAX) out.pick = raw.pick;
  if (typeof raw.doc === 'string' && DOC.test(raw.doc)) out.doc = raw.doc;
  // A contract only means something with a contract open, or alone, for Deals to find the deal it opens on.
  const contract = positiveInt(raw.contract);
  if (contract && (out.doc === undefined || out.doc.startsWith('contract:'))) out.contract = contract;
  if (raw.sheet === 'more') out.sheet = 'more';
  const team = positiveInt(raw.team);
  if (team) out.team = team;
  const playbook = positiveInt(raw.playbook);
  if (playbook) {
    out.playbook = playbook;
    if (typeof raw.at === 'string' && STAGE_ID.test(raw.at)) out.at = raw.at;
  }
  return out;
}
